import { useLazyQuery } from "@apollo/client";
import { Col, Row } from "antd";
import classNames from "classnames";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { get, isEmpty } from "lodash";
import moment from "moment";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { NAME_SPACES } from "shared/locales/constants";
import { FRANCE_ADDRESS_ZIP_CODE_CITY } from "utils/api/graphql/queries/referentials";
import {
  CONTACT_POINT_SYSTEM,
  DATE_FORMAT,
  PROFILE_LIST,
} from "utils/constants";
import { hundredYearsAgoStart } from "utils/helpers/date";
import { bindInputProps } from "utils/helpers/input";
import ActionButtons from "../Components/ActionButtons";
import Button from "../Components/Button";
import Header from "../Components/Header";
import Input from "../Components/Input";
import Layout from "../Components/Layout";
import Select from "../Components/Select";
import validation from "./validation";

function View({ isCollective, telecoms, contact, onNext, onBack }) {
  const { t } = useTranslation();

  const { handleSubmit, values, setFieldValue, ...formProps } = useFormik({
    initialValues: {
      telecoms,
      birthDate: get(contact, "user.birthDate") || null,
      profile: get(contact, "fields.profile"),
    },
    onSubmit: onNext,
    validationSchema: validation(
      t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, { returnObjects: true }),
    ),
    validateOnChange: true,
    validateOnBlur: true,
  });

  const [getCities, { data: cities, loading }] = useLazyQuery(
    FRANCE_ADDRESS_ZIP_CODE_CITY,
    {
      variables: {
        where: {
          zipCode: get(
            values,
            `telecoms.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
          ),
        },
      },
    },
  );

  useEffect(() => {
    const zipCode = get(
      values,
      `telecoms.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
    );
    if (!zipCode || zipCode?.length < 5) return;
    getCities({ where: zipCode });
  }, [get(values, `telecoms.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`)]);

  const isStudent = () => {
    const profile = get(values, "profile");
    const age = moment().diff(moment(get(values, "birthDate")), "Y");
    const legibleAge = age > 25 && age < 31;
    return (
      (profile === PROFILE_LIST.PARTICULAR ||
        profile === PROFILE_LIST.STUDENT) &&
      legibleAge
    );
  };

  return (
    <Layout>
      <Header
        title="Quelques informations pour personnaliser votre offre"
        highlight="personnaliser votre offre"
        subtitle="Informations de l’adhérent"
        onBack={onBack}
      />
      <Row justify="center">
        <Col xs={24} md={16} lg={12}>
          <div className="grid--1">
            <Input
              type="date"
              format={DATE_FORMAT}
              placeholder={"JJ / MM / AAAA"}
              label={"Date de naissance"}
              disabledDate={(current) =>
                current > dayjs().subtract(18, "years").endOf("year") ||
                current < hundredYearsAgoStart
              }
              value={values.birthDate ? dayjs(values.birthDate) : undefined}
              defaultPickerValue={
                !values.birthDate && dayjs().subtract(30, "years")
              }
              onChange={(date) => {
                const major = dayjs().subtract(18, "years").endOf("year");
                if (date?.isValid() && date.isBefore(major)) {
                  setFieldValue(
                    "birthDate",
                    date
                      .set("hour", 2)
                      .set("minute", 0)
                      .set("second", 0)
                      .toISOString(),
                  );
                }
              }}
              onBlur={({ target: { value } }) => {
                const major = dayjs().subtract(18, "years").endOf("year");
                const date = dayjs(value, DATE_FORMAT);
                if (date?.isValid() && date.isBefore(major))
                  setFieldValue(
                    "birthDate",
                    date ? date.add(2, "h").toISOString() : null,
                  );
              }}
            />
            {!isCollective && isStudent() && (
              <div className="beneficiary__wrapper--main">
                <div className="beneficiary__wrapper--main--left">
                  <span className="question">Êtes-vous étudiant ?</span>
                </div>
                <div className="--action d-flex">
                  <Button
                    size="medium"
                    type={classNames({
                      primary: get(values, "profile") === PROFILE_LIST.STUDENT,
                      default: get(values, "profile") !== PROFILE_LIST.STUDENT,
                    })}
                    onClick={() => {
                      setFieldValue("profile", PROFILE_LIST.STUDENT);
                    }}
                  >
                    Oui
                  </Button>

                  <Button
                    size="medium"
                    type={classNames({
                      primary: get(values, "profile") !== PROFILE_LIST.STUDENT,
                      default: get(values, "profile") === PROFILE_LIST.STUDENT,
                    })}
                    onClick={() => {
                      setFieldValue("profile", PROFILE_LIST.PARTICULAR);
                    }}
                  >
                    Non
                  </Button>
                </div>
              </div>
            )}
            <Input
              placeholder={"ex : 69001"}
              maxLength={5}
              label={"Code postal"}
              {...bindInputProps({
                name: `telecoms.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
                values,
                ...formProps,
              })}
              onChange={({ target: { value } }) => {
                setFieldValue(
                  `telecoms.${CONTACT_POINT_SYSTEM.CITY}.value`,
                  null,
                );
                setFieldValue(
                  `telecoms.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
                  value,
                );
              }}
            />
            {
              <Select
                showSearch
                loading={loading}
                options={get(cities, "franceAddressZipCodeCity.response", [])}
                placeholder={"Sélectionnez ..."}
                label={"Ville"}
                {...bindInputProps({
                  name: `telecoms.${CONTACT_POINT_SYSTEM.CITY}.value`,
                  values,
                  ...formProps,
                })}
              />
            }
          </div>
        </Col>
      </Row>

      <ActionButtons
        disabled={
          !get(values, `telecoms.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`) ||
          !values.birthDate ||
          !isEmpty(formProps?.errors)
        }
        onNext={handleSubmit}
        onBack={onBack}
      />
    </Layout>
  );
}

export default View;
